<script lang="ts" setup>
import type { Ref } from 'vue';
import type { BlockSerializedChild } from '#layers/block-engine/types/block';
import { useElementBounding } from '@vueuse/core';
import { computed, useId, useTemplateRef, watch } from 'vue';
import { useBlockLibraryStore } from '#layers/block-engine/composables/block-editor/library';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { useBlockSlots, useHorizontalLayout } from '#layers/block-engine/utils/block/api/helpers';
import { useSharedMouse } from '#layers/ui/public';

defineOptions({
	inheritAttrs: false,
});

const {
	child,
	slotName,
	index,
	horizontal,
	tree,
} = defineProps<{
	child?: BlockSerializedChild;
	slotName?: string;
	index: number;
	empty?: boolean;
	tree?: boolean;
	preview?: boolean;
	horizontal?: boolean;
	label?: string;
}>();

/**
 * Distance up to which the target is considered hovered.
 */
const HOVER_DISTANCE = 5;
/**
 * Maximum distance to the edges of the add button to show it.
 */
const MAX_DISTANCE = 100;

const { insertion, selection, drag, bounds } = useBlockEditorContext();

/**
 * Unique ID to identify this add button.
 */
const targetId = computed(() => `add-${child?.id}-${slotName}-${index}`);

const addButton = useTemplateRef('addButton');

const { slotButtonDisabled } = useIsBlockButtonDisabled();
const { horizontalLayout } = useDirection();
const { selected, displayingPreview } = useSelectionState();
const { hoveredVisuals } = useHovering(selected);
const { mouseDistance, addButtonOpacity } = useHiding();
useMouseHover(mouseDistance);

function addBlock() {
	insertion.target.value = {
		id: targetId.value,
		childId: child?.id,
		slotName,
		index,
	};

	selection.clear();
}

function useIsBlockButtonDisabled() {
	const { getBlock } = useBlockLibraryStore();
	const block = computed(() => child ? getBlock(child?.blockId) : undefined);
	const blockSlots = useBlockSlots(block, () => child, child?.props ?? {});
	const slotButtonDisabled = computed(() => blockSlots.value.some((slot) => slot.name === slotName && slot.showDefaultAddButton === false));

	return {
		slotButtonDisabled,
	};
}

function useDirection() {
	const injectedHorizontalLayout = useHorizontalLayout();
	const horizontalLayout = computed(() => horizontal || injectedHorizontalLayout.value);

	return {
		horizontalLayout,
	};
}

function useSelectionState() {
	const selected = computed(() => insertion.target.value?.id === targetId.value);
	const displayingPreview = computed(() => selected.value && insertion.blockId.value);

	return {
		selected,
		displayingPreview,
	};
}

function useHovering(selected: Ref<boolean>) {
	watch(selected, (value) => {
		if (value) {
			insertion.hoveredTargetId.value = null;
		}
	});

	const hoveredVisuals = computed(() => insertion.hoveredTargetId.value === targetId.value || drag.hoverDragTarget.value?.id === targetId.value);

	return {
		hoveredVisuals,
	};
}

function useHiding() {
	const { x, y } = useSharedMouse();
	const addButtonBounds = useElementBounding(addButton, {
		updateTiming: 'next-frame',
	});

	// Manhattan distance from the sides of the bounds
	const mouseDistance = computed(() => {
		const mouseX = x.value;
		const mouseY = y.value;
		const bounds = {
			x: addButtonBounds.left.value,
			y: addButtonBounds.top.value,
			width: addButtonBounds.width.value,
			height: addButtonBounds.height.value,
		};

		if (horizontalLayout.value) {
			bounds.width = tree ? 4 : 8;
		}
		else {
			bounds.height = tree ? 4 : 8;
		}

		let dx;

		if (mouseX < bounds.x) {
			dx = bounds.x - mouseX;
		}
		else if (mouseX > bounds.x + bounds.width) {
			dx = mouseX - (bounds.x + bounds.width);
		}
		else {
			dx = 0;
		}

		let dy;

		if (mouseY < bounds.y) {
			dy = bounds.y - mouseY;
		}
		else if (mouseY > bounds.y + bounds.height) {
			dy = mouseY - (bounds.y + bounds.height);
		}
		else {
			dy = 0;
		}

		const distanceToEdges = dx + dy;

		let distanceToCenter = 0;

		// Only compute the distance to center if the mouse is close to the edges
		if (distanceToEdges <= HOVER_DISTANCE) {
			const centerX = bounds.x + bounds.width * 0.5;
			const centerY = bounds.y + bounds.height * 0.5;
			const dx = centerX - mouseX;
			const dy = centerY - mouseY;
			distanceToCenter = dx * dx + dy * dy;
		}

		// Manhattan distance is faster!!
		return {
			toEdges: distanceToEdges,
			toCenter: distanceToCenter,
		};
	});

	const addButtonOpacity = computed(() => {
		if (selected.value || hoveredVisuals.value) {
			return 1;
		}

		if (mouseDistance.value.toEdges < MAX_DISTANCE) {
			return 1 - mouseDistance.value.toEdges / MAX_DISTANCE;
		}

		return 0;
	});

	bounds.updateEvent.on(() => {
		addButtonBounds.update();
	});

	return {
		mouseDistance,
		addButtonOpacity,
	};
}

function useMouseHover(mouseDistance: Ref<{ toEdges: number; toCenter: number }>) {
	const randomId = useId();

	watch(mouseDistance, ({ toEdges, toCenter }) => {
		if (toEdges <= HOVER_DISTANCE) {
			// We need to compute the closest distance to center to chose between different targets if they overlap each other
			// For example in the tree, the add buttons for the end of the children of a block and the next available position are overlapping
			// but the extra padding due to depth allows to distinguish between them

			if (insertion.hoveredTargetDistance.id === randomId) {
				insertion.hoveredTargetDistance.distance = toCenter;
			}
			else if (!insertion.hoveredTargetDistance.id || toCenter < insertion.hoveredTargetDistance.distance) {
				insertion.hoveredTargetId.value = targetId.value;
				insertion.hoveredTargetDistance.id = randomId;
				insertion.hoveredTargetDistance.distance = toCenter;

				if (drag.dragging.value) {
					drag.setHoverDragTarget({
						ownerId: targetId.value,
						target: {
							id: targetId.value,
							childId: child?.id,
							slotName,
							index,
						},
					});
				}
			}
		}
		// Reset if mouse leave
		else if (insertion.hoveredTargetDistance.id === randomId) {
			insertion.hoveredTargetId.value = null;
			insertion.hoveredTargetDistance.id = null;

			if (drag.dragging.value) {
				drag.clearHoverDragTarget(targetId.value);
			}
		}
	});
}
</script>

<template>
	<Transition
		leave-active-class="transition-opacity duration-fast"
		leave-to-class="opacity-0"
		appear
	>
		<template
			v-if="!displayingPreview"
		>
			<div
				v-if="empty && !selected && !slotButtonDisabled"
				class="relative z-10000 col-span-full flex items-center justify-center"
				data-block-editor-prevent-selection
				v-bind="$attrs"
			>
				<DButton
					:label="label ?? (slotName === 'default' || !slotName ? $t('builder.add_a_block') : $t('builder.add_a_block_to_slot', { slot: slotName }))"
					icon="lucide:circle-plus"
					variant="outline"
					color="neutral"
					size="sm"
					class="m-0.5"
					@click.stop="addBlock()"
				/>
			</div>
			<div
				v-else
				ref="addButton"
				class="pointer-events-none relative z-10000 col-span-full flex items-center justify-center"
				:class="[
					horizontalLayout ? 'h-full w-0 flex-col' : 'h-0 w-full',
					{
						'opacity-0! transition-opacity': tree && !hoveredVisuals && !selected,
						'my-0.5': tree,
					},
				]"
				:style="tree ? {} : {
					opacity: addButtonOpacity,
				}"
				v-bind="$attrs"
			>
				<div
					class="flex-1 bg-primary"
					:class="[
						horizontalLayout ? 'w-px' : 'h-px',
						{
							'opacity-0 transition-opacity': !hoveredVisuals && !selected && !tree,
						},
					]"
				/>
				<template v-if="!selected && !drag.dragging.value">
					<div
						class="group/add-btn pointer-events-auto -m-1 flex size-8 cursor-pointer items-center justify-center"
						:data-testid="`builder-add-block-${targetId}`"
						:class="{
							'pointer-events-none': !hoveredVisuals,
						}"
						@click.stop="addBlock()"
					>
						<div class="relative size-4">
							<div class="absolute inset-0 rounded-full bg-default group-hover/add-btn:bg-primary" />
							<div class="absolute inset-0.5 rounded-full bg-default" />
							<DIcon
								name="lucide:circle-plus"
								class="absolute top-0 left-0 size-full text-primary"
							/>
						</div>
					</div>
					<div
						class="flex-1 bg-primary"
						:class="[
							horizontalLayout ? 'w-px' : 'h-px',
							{
								'opacity-0 transition-opacity duration-150': !hoveredVisuals && !tree,
							},
						]"
					/>
				</template>
			</div>
		</template>
	</Transition>
</template>
